import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { usersApi } from '../api';
import { FiPlus, FiTrash2, FiUser, FiMoreVertical } from 'react-icons/fi';
import toast from 'react-hot-toast';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('admin');
  const [saving, setSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuRef]);

  const fetchUsers = async () => {
    try {
      const { data } = await usersApi.list();
      setUsers(data.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await usersApi.create({ email, name, password, role });
      toast.success('User created');
      setShowForm(false);
      setEmail(''); setName(''); setPassword('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if(!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await usersApi.delete(id);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
    setOpenMenuId(null);
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Team Users</h1>
          <p className="text-muted-foreground text-sm">Manage manager panel administrators</p>
        </div>
        <button 
          onClick={() => setShowForm(!showForm)} 
          className="h-10 px-4 py-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors flex items-center gap-2"
        >
          <FiPlus size={16} /> Add User
        </button>
      </div>

      {showForm && (
        <div className="card p-6 mb-6">
          <h3 className="text-lg font-semibold tracking-tight text-foreground mb-4">Create New User</h3>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">Name</label>
              <input type="text" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" required />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">Email</label>
              <input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" required />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">Password</label>
              <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" required minLength={6} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <button type="submit" disabled={saving} className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
              {saving ? 'Creating...' : 'Create'}
            </button>
          </form>
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="overflow-x-auto min-h-[250px]">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Name</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Email</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Role</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Status</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Last Login</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-right"></th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {users.map(u => (
                  <tr key={u.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="p-4 align-middle font-medium text-foreground flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground">
                        <FiUser size={12} />
                      </div>
                      {u.name}
                    </td>
                    <td className="p-4 align-middle text-muted-foreground">{u.email}</td>
                    <td className="p-4 align-middle">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                        u.role === 'super_admin' ? 'bg-secondary text-secondary-foreground border-transparent' : 'bg-muted text-foreground border-transparent'
                      }`}>
                        {u.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-4 align-middle">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                        u.is_active ? 'bg-secondary text-secondary-foreground border-transparent' : 'bg-destructive/10 text-destructive border-transparent'
                      }`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 align-middle text-muted-foreground">
                      {u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}
                    </td>
                    <td className="p-4 align-middle text-right relative">
                      <button 
                        onClick={() => setOpenMenuId(openMenuId === u.id ? null : u.id)}
                        className="p-2 text-muted-foreground hover:text-foreground rounded-md transition-colors inline-flex"
                      >
                        <FiMoreVertical size={16} />
                      </button>
                      
                      {openMenuId === u.id && (
                        <div ref={menuRef} className="absolute right-6 top-8 w-32 bg-popover text-popover-foreground rounded-md shadow-md border border-border z-50 py-1 animate-in fade-in slide-in-from-top-1">
                          <button onClick={() => handleDelete(u.id)} className="w-full text-left px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2">
                            <FiTrash2 size={14} /> Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
